import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

import PlannedBedbankOfferPackageSummary from './PlannedBedbankPackageSummary'
import PlannedLeOfferPackageSummary from './PlannedLeOfferPackageSummary'
import BookingsDetails from '../Common/BookingsDetails'
import ItemTypeAndBookedLabel from '../Common/ItemTypeAndBookedLabel'

import Hr from 'components/Common/Hr'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import offerPageURL from 'lib/offerPageURL'
import { isBedbank, isLEOffer } from 'lib/offerTypes'
import BedbankOfferPackageSummary from 'tripPlanner/components/TripModal/TripItems/Summary/AccommodationSummary/BedbankOfferPackageSummary'
import LeOfferPackageSummary from 'tripPlanner/components/TripModal/TripItems/Summary/AccommodationSummary/LeOfferPackageSummary'
import AddressWithViewMapButton from 'tripPlanner/components/TripModal/TripItems/Summary/Common/AddressWithViewMapButton'
import CheckInAndOutDate from 'tripPlanner/components/TripModal/TripItems/Summary/Common/CheckInAndOutDate'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useOrder } from 'tripPlanner/hooks/api'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import { AirbnbItem, HotelItem } from 'tripPlanner/types/tripItem'
import {
  getOrderId,
  isHotel,
  isLEOfferNoSummary,
  maybePlaceId,
} from 'tripPlanner/utils'
import { itemSectionId } from 'tripPlanner/utils/itemSection'

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: 0 0 ${rem(12)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutral.six};
`

const Section = styled.div`
  padding: ${rem(20)} 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutral.six};
`

interface Props {
  item: AirbnbItem | HotelItem
  offer: App.Offer | App.BedbankOffer
}

const AccommodationItemSummaryContent = ({ item, offer }: Props) => {
  const { setImmersiveMode } = useImmersiveModeHandlers()
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false

  const onOfferLinkClick = useCallback(() => {
    if (canEdit) {
      setImmersiveMode({
        tripId: item.tripId,
        sectionId: itemSectionId(item.id),
      })
    }
  }, [setImmersiveMode, item, canEdit])

  const haveAnyDates = item.startDate || item.endDate

  const orderId = isHotel(item) ? getOrderId(item) : undefined

  const { data: order } = useOrder(orderId)

  const offerUrl = offer && offerPageURL(offer)

  const hasOfferDetails = offer && (isLEOffer(offer) || isBedbank(offer))

  const willDisplayOfferAddress = hasOfferDetails && offer.property?.address
  const willDisplayItemAddress = !hasOfferDetails && item.place.address

  const plannedPackageId =
    item.type === 'HOTEL' ? item.savedItemData?.packageId : undefined

  const lePlannedPackage =
    plannedPackageId &&
    !order &&
    isLEOfferNoSummary(offer) &&
    offer.packages.find((offerPackage) => offerPackage.id === plannedPackageId)
  const bedbankPlannedPackage =
    plannedPackageId &&
    !order &&
    !isLEOfferNoSummary(offer) &&
    offer.packages.find(
      (offerPackage) => offerPackage.roomId === plannedPackageId,
    )
  return (
    <ModalContent>
      <VerticalSpacer gap={20}>
        <ItemTypeAndBookedLabel item={item} />
        <Header>
          {offer && (
            <div>
              {offer?.type && (
                <ProductTypeLabel productType={offer.productType} />
              )}
            </div>
          )}
          {hasOfferDetails && (
            <BodyText variant="large">{offer?.name}</BodyText>
          )}
          {offerUrl && (
            <TextLink weight="bold" to={offerUrl} onClick={onOfferLinkClick}>
              View offer details
            </TextLink>
          )}
        </Header>
        {order && (
          <Section>
            <BookingsDetails order={order} orderItemType="offer" />
          </Section>
        )}
        {(haveAnyDates ||
          willDisplayOfferAddress ||
          willDisplayItemAddress) && (
          <Section>
            {haveAnyDates && (
              <CheckInAndOutDate
                isAccommodation
                startDate={item.startDate}
                endDate={item.endDate}
                startDay={item.startDay}
                endDay={item.endDay}
                startTime={item.startTime}
                endTime={item.endTime}
              />
            )}
            {!!offer.property && (
              <AddressWithViewMapButton address={offer.property.address} />
            )}
            {!hasOfferDetails && item.place.address && (
              <AddressWithViewMapButton
                placeId={maybePlaceId(item.place)}
                address={item.place.address}
              />
            )}
          </Section>
        )}
        {order && isLEOfferNoSummary(offer) && (
          <LeOfferPackageSummary offer={offer} order={order} />
        )}
        {order && !isLEOfferNoSummary(offer) && (
          <BedbankOfferPackageSummary offer={offer} order={order} />
        )}
        {lePlannedPackage &&
          item.type === 'HOTEL' &&
          item.savedItemData?.occupancies?.map((_, index) => (
            <>
              {index !== 0 && <Hr colour="light-grey" />}
              <PlannedLeOfferPackageSummary
                key={index}
                offer={offer}
                pkg={lePlannedPackage}
              />
            </>
          ))}
        {bedbankPlannedPackage &&
          item.type === 'HOTEL' &&
          item.savedItemData?.occupancies?.map((_, index) => (
            <>
              {index !== 0 && <Hr colour="light-grey" />}
              <PlannedBedbankOfferPackageSummary
                key={index}
                offer={offer}
                pkg={bedbankPlannedPackage}
              />
            </>
          ))}
      </VerticalSpacer>
    </ModalContent>
  )
}

export default AccommodationItemSummaryContent
